/** @file parseHash: the editor Route named by a location hash (`#/u/<puppet>/pose`); unknown hashes go home. */
import type { PuppetScreen, Route } from '../../state/editorTypes';

const PUPPET_SCREENS: ReadonlySet<string> = new Set<PuppetScreen>(['pose', 'art', 'agent']);

/** `tile/<drawable>/<entry>/<tile>` after the puppet id: that Tile tab (entry and tile are non-negative integers). */
function tileRoute(puppetId: string, [drawable, entry, tile, ...rest]: string[]): Route {
  if (drawable === undefined || entry === undefined || tile === undefined || rest.length > 0) return { screen: 'projects' };
  if (!/^\d+$/.test(entry) || !/^\d+$/.test(tile)) return { screen: 'projects' };
  return { screen: 'tile', puppetId, tile: { drawable: decodeURIComponent(drawable), entry: Number(entry), tile: Number(tile) } };
}

/** `<id>`, `<id>/<screen>` or `<id>/tile/...` after `#/u/`: that puppet's screen (pose by default); anything else → projects. */
function puppetRoute([id, screen, ...rest]: string[]): Route {
  if (id === undefined) return { screen: 'projects' };
  const puppetId = decodeURIComponent(id);
  if (screen === 'tile') return tileRoute(puppetId, rest);
  if (rest.length > 0) return { screen: 'projects' };
  if (screen === undefined) return { screen: 'pose', puppetId };
  return PUPPET_SCREENS.has(screen) ? { screen: screen as PuppetScreen, puppetId } : { screen: 'projects' };
}

/**
 * `#/projects` and `''` → projects, `#/settings` → settings, `#/p/<project>` → that project's puppets,
 * `#/u/<puppet>` → that puppet's pose screen, `#/u/<puppet>/pose|art|agent` → that screen,
 * `#/u/<puppet>/tile/<drawable>/<entry>/<tile>` → that Tile tab; anything else → projects.
 * Ids are URI-decoded.
 */
export function parseHash(hash: string): Route {
  const parts = hash.replace(/^#?\/?/, '').split('/').filter((p) => p !== '');
  const [head, id] = parts;
  if (head === 'settings' && parts.length === 1) return { screen: 'settings' };
  if (head === 'p' && id !== undefined && parts.length === 2) return { screen: 'puppets', projectId: decodeURIComponent(id) };
  return head === 'u' ? puppetRoute(parts.slice(1)) : { screen: 'projects' };
}
